import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, TrendingUp } from "lucide-react";
import { publicShareApi, type ApiError, type PublicStats } from "../lib/api";
import { useI18n } from "../lib/i18n";
import FinPipMark from "../components/FinPipMark";
import ThemeToggle from "../components/ThemeToggle";

/**
 * Public, logged-out-friendly stats page — the viral loop. Anyone with a share
 * link sees a trader's NORMALIZED headline numbers + a time-weighted
 * performance index curve, plus a prominent CTA to start their own free
 * journal. No auth required, and — by design — NO absolute dollars anywhere:
 * no balance, no deposits/withdrawals, no dollar P/L. Only percentages, a
 * profit-factor ratio, a trade count, and a performance index (starts at 100).
 */
export default function PublicStatsPage() {
  const { t } = useI18n();
  const { token } = useParams<{ token: string }>();

  const q = useQuery({
    queryKey: ["public-stats", token],
    queryFn: () => publicShareApi.stats(token as string),
    enabled: !!token,
    retry: false,
  });

  const notFound = q.isError && (q.error as ApiError)?.status === 404;

  return (
    <div className="min-h-screen px-4 py-10">
      <div className="absolute top-4 end-4">
        <ThemeToggle compact />
      </div>

      <div className="mx-auto w-full max-w-3xl">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-6">
          <FinPipMark className="w-10 h-10 text-[#0c34f4] dark:text-white" />
          <div>
            <p className="text-xl font-bold text-parchment tracking-tight">FinPip Journal</p>
            <p className="text-parchment-muted text-xs">{t("login.tagline")}</p>
          </div>
        </div>

        {q.isLoading && (
          <div className="glass p-10 text-center text-parchment-muted text-sm">
            {t("common.loading")}
          </div>
        )}

        {notFound && (
          <section className="glass-gold p-8 text-center">
            <h1 className="text-2xl font-semibold text-parchment">{t("public.notFoundTitle")}</h1>
            <p className="mt-2 text-parchment-muted">{t("public.notFoundBody")}</p>
            <a href="/journal" className="btn-gold mt-6 inline-flex">
              {t("public.cta")}
              <ArrowRight className="w-4 h-4" />
            </a>
          </section>
        )}

        {q.isError && !notFound && (
          <div className="glass p-10 text-center text-pnl-neg text-sm">{t("public.loadError")}</div>
        )}

        {q.data && (
          <>
            <section className="glass-gold p-6 sm:p-8">
              <p className="text-parchment-muted text-xs uppercase tracking-widest flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-accent" />
                {t("public.eyebrow")}
              </p>
              <h1 className="text-3xl font-bold text-parchment mt-1">
                {t("public.heading", { name: q.data.display_name })}
              </h1>
              <p className="text-parchment-muted text-sm mt-1">{t("public.subheading")}</p>

              {/* Headline stat cards — normalized only, no dollars */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                <StatCard
                  label={t("public.totalReturn")}
                  value={formatSignedPct(q.data.headline.total_return_pct)}
                  tone={q.data.headline.total_return_pct >= 0 ? "pos" : "neg"}
                />
                <StatCard
                  label={t("public.winRate")}
                  value={`${q.data.headline.win_rate_pct.toFixed(1)}%`}
                />
                <StatCard
                  label={t("public.profitFactor")}
                  value={
                    q.data.headline.profit_factor != null
                      ? q.data.headline.profit_factor.toFixed(2)
                      : "—"
                  }
                />
                <StatCard label={t("public.trades")} value={String(q.data.headline.trade_count)} />
              </div>
            </section>

            {/* Time-weighted performance index (starts at 100) */}
            <section className="glass p-6 mt-6">
              <h2 className="text-lg font-semibold text-parchment mb-1 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-accent" />
                {t("public.performanceIndex")}
              </h2>
              <p className="text-parchment-muted text-xs mb-4">{t("public.performanceIndexHint")}</p>
              {q.data.series.length > 0 ? (
                <PerformanceChart series={q.data.series} />
              ) : (
                <div className="text-center text-parchment-muted text-sm py-10">
                  {t("public.noTrades")}
                </div>
              )}
            </section>

            {/* Growth-loop CTA */}
            <section className="glass-gold p-6 sm:p-8 mt-6 text-center">
              <h2 className="text-xl font-semibold text-parchment">{t("public.ctaTitle")}</h2>
              <p className="text-parchment-muted text-sm mt-1">{t("public.ctaBody")}</p>
              <a href="/journal" className="btn-gold mt-5 inline-flex">
                {t("public.cta")}
                <ArrowRight className="w-4 h-4" />
              </a>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

/** Signed percentage, e.g. +12.3% / −4.5%. */
function formatSignedPct(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toFixed(2)}%`;
}

/**
 * Lightweight inline SVG area/line chart of the performance index series.
 * The index starts at 100; we draw a subtle gold stroke + area fill and a
 * dashed baseline at 100. Responsive via viewBox + width:100%. No dollars.
 */
function PerformanceChart({ series }: { series: PublicStats["series"] }) {
  const W = 720;
  const H = 240;
  const padX = 8;
  const padY = 16;

  const values = series.map((p) => p.index);
  const min = Math.min(100, ...values);
  const max = Math.max(100, ...values);
  const span = max - min || 1;

  const n = series.length;
  const x = (i: number) => padX + (n <= 1 ? 0 : (i / (n - 1)) * (W - padX * 2));
  const y = (v: number) => padY + (1 - (v - min) / span) * (H - padY * 2);

  const linePts = series.map((p, i) => `${x(i).toFixed(2)},${y(p.index).toFixed(2)}`);
  const linePath = `M${linePts.join(" L")}`;
  const areaPath =
    `M${x(0).toFixed(2)},${(H - padY).toFixed(2)} ` +
    `L${linePts.join(" L")} ` +
    `L${x(n - 1).toFixed(2)},${(H - padY).toFixed(2)} Z`;

  const baselineY = y(100);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-56"
      role="img"
      aria-label="Performance index"
    >
      <defs>
        <linearGradient id="perfFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgb(var(--accent))" stopOpacity="0.28" />
          <stop offset="100%" stopColor="rgb(var(--accent))" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* baseline at index 100 */}
      <line
        x1={padX}
        y1={baselineY}
        x2={W - padX}
        y2={baselineY}
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="1"
        strokeDasharray="4 4"
        vectorEffect="non-scaling-stroke"
        className="text-parchment-muted"
      />

      <path d={areaPath} fill="url(#perfFill)" />
      <path
        d={linePath}
        fill="none"
        stroke="rgb(var(--accent))"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "pos" | "neg";
}) {
  return (
    <div className="rounded-xl border border-glass bg-black/5 dark:bg-white/5 px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-parchment-muted">{label}</p>
      <p
        className={
          "text-xl font-bold mt-1 tabular " +
          (tone === "pos" ? "text-pnl-pos" : tone === "neg" ? "text-pnl-neg" : "text-parchment")
        }
      >
        {value}
      </p>
    </div>
  );
}
